import { ReactNode, PureComponent, ReactElement } from 'react';
import _ from 'lodash';
import * as WidgetsThingsStructs from "../WidgetsThingsStructs";
import { SettingsList } from '@Primitive/SettingsList';
import { SettingsGroup, SettingsType } from '@Primitive/SettingsListType';
import { IWidgetThingsClass } from "../Utils/IWidgetThingsClass";
import { getRowMinHeight } from '@Primitive/Typography';
import { getBool, getRandomSmallKey, isDefined, IsNullOrEmpty } from '@Utils/Micro';
import { IWidgetDescription, IWidgetSettingsProps, WidgetDescriptionToPlegmaThing } from '@UI/Widgets/WidgetStructs';
import { WebNode } from '@API/WebNode';
import { WebThing, WebThingPort } from '@API/WebThing';
import { Plegma } from '@API/Api';
import { WebThingPortType } from '@API/WebNodeTypes';
import Toggle from "@Primitive/Toggle";
// =======================================================================================================
export interface SwitchData {
    Switches: {
        Key: string;
        Value: string;
    }[];
    FillWidget: boolean;
    Padding: {
        Top: number;
        Bottom: number;
        Left: number;
        Right: number;
    }
}
// ------------------------------------------------------------------------------------------------------
class Switch extends PureComponent<WidgetsThingsStructs.PartialIWidgetThingsProps<SwitchData>> {
    constructor(props: WidgetsThingsStructs.PartialIWidgetThingsProps<SwitchData>) {
        super(props);
    }
    // ---------------------------------------------------------------------------------------------------
    render() {
        let minHeight = undefined;
        let widgetData = this.props.WidgetData;
        let containerPadding = WidgetsThingsStructs.defaultWidgetContainerPadding;
        if (this.props.WidgetData.FillWidget)
            minHeight = getRowMinHeight(
                widgetData.Switches.length, this.props.height, 30,
                containerPadding * 2 + widgetData.Padding.Top + widgetData.Padding.Bottom);

        return (
            <div style={{
                paddingTop: containerPadding + widgetData.Padding.Top,
                paddingBottom: containerPadding + widgetData.Padding.Bottom,
                paddingLeft: containerPadding + widgetData.Padding.Left,
                paddingRight: containerPadding + widgetData.Padding.Right
            }}>
                {_.map(widgetData.Switches, sw => {
                    let port = this.props.PortsStates[sw.Key];
                    let label = this.props.LocalizationMap[sw.Key];
                    if (!isDefined(label))
                        label = sw.Value;

                    return <Toggle
                        onToggle={(isInputChecked) => this.props.SendPortEvent(sw.Key, isInputChecked)}
                        key={sw.Key}
                        label={label}
                        toggled={isDefined(port) && getBool(port.State)}
                        style={{
                            height: minHeight,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start'
                        }} />;
                })}
            </div>
        );
    }
}
// =======================================================================================================
class SwitchWidgetSettings extends PureComponent<IWidgetSettingsProps<SwitchData>> {
    constructor(props: IWidgetSettingsProps<SwitchData>) {
        super(props);
    }
    // ---------------------------------------------------------------------------------------------------
    render() {
        let settings: SettingsGroup<SwitchData>[] = [{
            name: "",
            elements: [{
                type: SettingsType.ItemsMgmtDialog,
                label: 'Switches',
                tooltip: 'Specify the desired number of switches and their port names.',
                dataPath: "Switches",
                dialogStaticSize: { w: 0.7, h: 0.8 },
                itemsInfo: [
                    {
                        type: SettingsType.String,
                        label: "Label",
                        dataPath: "Value"
                    }
                ],
                createNewItem: () => {
                    let thing = WebNode.GetInstanceKey(this.props.NodeKey).GetThing(this.props.ThingKey);
                    return { Key: thing.GetNextGeneratedPortKey(), Value: "New Switch" }
                }
            },
            {
                type: SettingsType.Boolean,
                label: "Fill Space",
                tooltip: "Fills the widget's available space.",
                dataPath: "FillWidget"
            }]
        },
        {
            name: "Padding",
            elements: [{
                type: SettingsType.Slider,
                label: "Top",
                tooltip: 'Sets maximum padding-top',
                dataPath: "Padding.Top",
                maxValue: 50,
                minValue: 0,
                stepValue: 1,
                valuePosfix: "px"
            },
            {
                type: SettingsType.Slider,
                label: "Bottom",
                tooltip: 'Sets maximum padding-bottom',
                dataPath: "Padding.Bottom",
                maxValue: 50,
                minValue: 0,
                stepValue: 1,
                valuePosfix: "px"
            },
            {
                type: SettingsType.Slider,
                label: "Left",
                tooltip: 'Sets maximum padding-left',
                dataPath: "Padding.Left",
                maxValue: 50,
                minValue: 0,
                stepValue: 1,
                valuePosfix: "px"
            },
            {
                type: SettingsType.Slider,
                label: "Right",
                tooltip: 'Sets maximum padding-right',
                dataPath: "Padding.Right",
                maxValue: 50,
                minValue: 0,
                stepValue: 1,
                valuePosfix: "px"
            }]
        }];

        return <SettingsList
            AutoSave={true}
            SettingsMap={settings}
            Data={this.props.WidgetData}
            onSaveData={(data) => {
                if (isDefined(this.props.onWidgetDataUpdate))
                    this.props.onWidgetDataUpdate(data);
            }}
            Toolbar={this.props.SettingListToolbar}
        />;
    }
    // ---------------------------------------------------------------------------------------------------
}

// =======================================================================================================
// Switch Description interface

export class SwitchWidget extends IWidgetThingsClass<SwitchData> {
    // ------------------------------------------------------------------------------------------------
    public Render(key: string, width: number, height: number): ReactNode {
        return <Switch />;
    }
    // ------------------------------------------------------------------------------------------------
    public RenderWidgetSettings(key: string, width: number, height: number): ReactElement<any> {
        const parent = this.Parent;
        return <SwitchWidgetSettings
            key={"Settings-" + key}
            width={width}
            height={height}
            WidgetData={_.cloneDeep(this.WidgetData)} // copy of the data and is return the copy in the update cb
            WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
            ThingKey={this.ThingKey}
            NodeKey={this.NodeKey}
            onWidgetDataUpdate={(d) => this.WidgetDataUpdate(d)}
            SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null}
        />;
    }
    // ------------------------------------------------------------------------------------------------
    updateThing() {
        let wData = this.WidgetData;
        let webNode = WebNode.GetInstanceKey(this.NodeKey);
        let thing = webNode.GetThing(this.ThingKey);

        // Clean old ports
        thing.Ports = {};

        // Add the ports
        wData.Switches.forEach((sw) => {
            thing.AddPort(new WebThingPort(
                {
                    Name: sw.Value,
                    PortKey: sw.Key,
                    Type: Plegma.ePortType.Boolean,
                    ioDirection: Plegma.ioPortDirection.InputOutput,
                    State: "False"
                },
                WebThingPortType.Data
            ), false);
        });

        webNode.GetModule(this.WebModuleKey).UpdateThing(thing);
    }
    // ------------------------------------------------------------------------------------------------
    initThing() {
        let wData = this.WidgetData;

        // Thing description
        let plegmaThing = WidgetDescriptionToPlegmaThing(this.Description);

        // Ports
        let ports: WebThingPort[] = [];

        // Add all the news
        wData.Switches.forEach((sw) => {
            // Add the new port
            ports.push(new WebThingPort(
                {
                    Name: sw.Value,
                    PortKey: sw.Key,
                    Type: Plegma.ePortType.Boolean,
                    ioDirection: Plegma.ioPortDirection.InputOutput,
                    State: "False"
                },
                WebThingPortType.Data
            ));
        });

        // Create the thing
        let thing = new WebThing(this.WebModule, plegmaThing, ports);
        this.ThingKey = thing.ThingKey;
    }
    // ------------------------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(): SwitchData {
        return {
            Switches: [],
            FillWidget: false,
            Padding: {
                Top: 0,
                Bottom: 0,
                Left: 0,
                Right: 0
            }
        } as SwitchData;
    }
    // ------------------------------------------------------------------------------------------------
    public GetDefaultWidgetData() { return SwitchWidget.GetDefaultWidgetDataStatic(); }
    // ------------------------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        let data: SwitchData = SwitchWidget.GetDefaultWidgetDataStatic();
        // fill sample switches
        data.Switches = [
            { Key: "1_" + getRandomSmallKey(), Value: "Switch 1" },
            { Key: "2_" + getRandomSmallKey(), Value: "Switch 2" },
            { Key: "3_" + getRandomSmallKey(), Value: "Switch 3" }
        ];
        return {
            Title: "Switch List",
            WidgetTitle: "",
            Key: "SwitchWidget",
            Type: "SwitchWidget",
            Data: data,
            MinH: 1,
            MinW: 1,
            ShowExtraMenu: false,
            IconURI: WidgetsThingsStructs.IconURIPath + "/png/switch.png"
        };
    }
    // ------------------------------------------------------------------------------------------------
    public GetLocalizationDictionary() {
        let res = {};

        if (!IsNullOrEmpty(this.Description.WidgetTitle))
            res[WidgetsThingsStructs.WidgetLocalizationKeys.Title] = this.Description.WidgetTitle;

        _.forEach(this.WidgetData.Switches, (b) => {
            res[b.Key] = b.Value;
        });

        return res;
    }
    // ------------------------------------------------------------------------------------------------
}
// =======================================================================================================